/**
 * Editable list of email/phone contacts with primary selection.
 */
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../shell/Button.js';
import { Input, Select } from '../../shell/Field.js';
import { CONTACT_LABEL } from '@shared/enums/index.js';
import type { ContactKind } from '@shared/types';

export type ContactDraft = {
  key: string;
  kind: ContactKind;
  label: string;
  value: string;
  isPrimary: boolean;
  personName?: string;
};

let draftKey = 0;
export function newContactDraft(
  kind: ContactKind,
  defaults?: Partial<ContactDraft>,
): ContactDraft {
  draftKey += 1;
  return {
    key: `c-${draftKey}`,
    kind,
    label: defaults?.label ?? (kind === 'phone' ? 'Mobile' : 'Work'),
    value: defaults?.value ?? '',
    isPrimary: defaults?.isPrimary ?? false,
    personName: defaults?.personName,
  };
}

type Props = {
  kind: ContactKind;
  items: ContactDraft[];
  onChange: (items: ContactDraft[]) => void;
  /** Show person-name field (vendors). */
  showPersonName?: boolean;
  error?: string;
};

export function ContactListEditor({
  kind,
  items,
  onChange,
  showPersonName = false,
  error,
}: Props) {
  const label = kind === 'email' ? 'Emails' : 'Phone numbers';

  const update = (key: string, patch: Partial<ContactDraft>) => {
    onChange(
      items.map((item) => {
        if (item.key !== key) {
          if (patch.isPrimary) return { ...item, isPrimary: false };
          return item;
        }
        return { ...item, ...patch };
      }),
    );
  };

  const remove = (key: string) => {
    const next = items.filter((i) => i.key !== key);
    if (next.length > 0 && !next.some((i) => i.isPrimary)) {
      next[0]!.isPrimary = true;
    }
    onChange(next);
  };

  const add = () => {
    onChange([
      ...items,
      newContactDraft(kind, { isPrimary: items.length === 0 }),
    ]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</p>
        <Button type="button" size="sm" variant="ghost" onClick={add}>
          <Plus className="h-3.5 w-3.5" /> Add
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          No {kind === 'email' ? 'emails' : 'phone numbers'} yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.key}
              className="grid gap-2 rounded-lg border border-slate-200 p-2 dark:border-slate-700 sm:grid-cols-[7rem_1fr_auto]"
            >
              <Select
                value={item.label}
                onChange={(e) => update(item.key, { label: e.target.value })}
                aria-label="Label"
              >
                {CONTACT_LABEL.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </Select>
              <div className="space-y-2">
                {showPersonName && (
                  <Input
                    placeholder="Contact person (optional)"
                    value={item.personName ?? ''}
                    onChange={(e) => update(item.key, { personName: e.target.value })}
                  />
                )}
                <Input
                  type={kind === 'email' ? 'email' : 'tel'}
                  placeholder={kind === 'email' ? 'name@example.com' : '(555) 123-4567'}
                  value={item.value}
                  onChange={(e) => update(item.key, { value: e.target.value })}
                  required={kind === 'email'}
                />
                <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                  <input
                    type="radio"
                    name={`primary-${kind}`}
                    checked={item.isPrimary}
                    onChange={() => update(item.key, { isPrimary: true })}
                  />
                  Primary {kind}
                </label>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label="Remove"
                onClick={() => remove(item.key)}
                disabled={kind === 'email' && items.length <= 1}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
